import HoverTooltip from "../HoverTooltip.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatUsd } from "../../lib/renderers.js";
import { SEVERITY_COLOR } from "../../lib/traceReview.js";
import { GROUP_LABEL, GROUP_NOTE, REASON_LABEL, STOPPED_LABEL } from "../../lib/investigateFacts.js";
import { NOTE_KIND_LABEL } from "../../lib/investigateWorkspace.js";

// The Investigate tab's own marks, in the lens's vocabulary: a group's head,
// what a note says of its moment and a dig's answer.

// Costs as the rest of the app writes them ("$0.29", "<$0.01").
export const usd = formatUsd;
const plural = (n, one, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

// A note's kind in the colours the workspace draws it in: what the review
// and its check missed or got wrong as loud as a high finding, what matters
// as a medium one, what does not in the quiet tone.
export const NOTE_KIND_COLOR = {
  missed: SEVERITY_COLOR.danger,
  corrects: SEVERITY_COLOR.danger,
  matters: SEVERITY_COLOR.warn,
  doesnt: "var(--muted)",
  note: "var(--ink)",
};
// A word in the kind's colour, the way a dig's answer is a word: a note's own
// line carries it, so no box crowds the note beside it.
export function NoteKind({ kind }) {
  return (
    <span data-investigate-note-kind={kind} className="shrink-0 text-[9px] leading-[15px] tracking-[0.08em] uppercase" style={{ color: NOTE_KIND_COLOR[kind] || "var(--ink)" }}>
      {NOTE_KIND_LABEL[kind] || "Note"}
    </span>
  );
}

// Worded apart from the Needs you group: that one is a standard met, this the
// dig's own answer.
const NEEDS = {
  yes: ["Follow up", "var(--ink)"],
  unsure: ["Unclear", "var(--muted)"],
  no: ["Can be let go", "var(--muted-soft)"],
};
// What a dig said of the case, as a word.
export function DigWord({ dig }) {
  if (!dig) return null;
  if (dig.status === "failed") return <span data-investigate-dig="failed" className="shrink-0 text-[9px] leading-[15px] tracking-[0.08em] uppercase" style={{ color: SEVERITY_COLOR.warn }}>Dig failed</span>;
  if (dig.status !== "done" || !NEEDS[dig.needsPerson]) return null;
  const [word, color] = NEEDS[dig.needsPerson];
  return (
    <HoverTooltip label={dig.why || word} placement="top" wide>
      <span data-investigate-dig={dig.needsPerson} className="shrink-0 text-[9px] leading-[15px] tracking-[0.08em] uppercase" style={{ color }}>{word}</span>
    </HoverTooltip>
  );
}

// A group's head: its name, how much it holds, what puts a finding there,
// and the different things it holds, each counted. It counts the findings
// placed in it, as the flow above the tab does: `elsewhere` of them sit in
// cases listed above, and `moved` were placed here by Investigate rather than
// the backend's check. Without `findings` (still being read) it names the
// group and what puts a finding there.
export function GroupHead({ group, cases, findings = null, elsewhere = 0, moved = 0, reasons = {}, action = null, quiet = false }) {
  const labels = REASON_LABEL[group] || {};
  const parts = Object.entries(reasons).filter(([r, n]) => n > 0 && labels[r]).sort((a, b) => b[1] - a[1]);
  return (
    <div data-investigate-group-head={group} className="grid gap-[3px] px-[6px] pt-[14px] pb-[6px]">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-[10px] gap-y-[2px]">
        <span className={`text-[10px] leading-[1.2] tracking-[0.08em] uppercase ${quiet ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>{GROUP_LABEL[group]}</span>
        {findings == null ? null : (
          <span data-investigate-group-count="" className="text-[11px] leading-[1.2] tabular-nums text-[color:var(--muted)]">
            {plural(findings, "finding")}{cases ? ` in ${plural(cases, "case")}` : ""}
            {elsewhere ? ` \u00b7 ${elsewhere.toLocaleString()} in cases above` : ""}
            {moved ? ` \u00b7 ${moved.toLocaleString()} placed by Investigate` : ""}
          </span>
        )}
        <span className={NOTE_CLASS}>{GROUP_NOTE[group]}</span>
        {action ? <span className="ml-auto">{action}</span> : null}
      </div>
      {parts.length > 1 || (parts.length === 1 && group !== "slipped" && group !== "refused") ? (
        <div data-investigate-reasons="" className="flex flex-wrap gap-x-[12px] gap-y-[2px] text-[11px] leading-[1.3] text-[color:var(--muted)]">
          {parts.map(([r, n]) => (
            <span key={r} data-investigate-reason={r}><strong className="font-medium text-[color:var(--ink)]">{n.toLocaleString()}</strong> {labels[r].toLowerCase()}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Case rows still being read, shaped like the rows they stand for: flat bars
// that fade down the list, as the app's tables hold their rows' place.
const PENDING_WIDTHS = [[46, 64], [58, 52], [38, 70]];
export function PendingCases({ rows = 2 }) {
  return (
    <div data-investigate-pending="" aria-hidden="true" className="grid min-w-0">
      {Array.from({ length: rows }, (_, i) => {
        const [title, line] = PENDING_WIDTHS[i % PENDING_WIDTHS.length];
        return (
          <div key={i} className="flex min-w-0 items-center gap-x-[12px] border-t border-[color:var(--line-soft)] px-[6px] py-[8px]" style={{ opacity: Math.max(20, 70 - i * 15) / 100 }}>
            <span className="h-[9px] w-[9px] shrink-0 rounded-[2px] bg-[color:var(--line-soft)]" />
            <span className="h-[17px] w-[36px] shrink-0 rounded-[2px] bg-[color:var(--line-soft)]" />
            <span className="grid min-w-0 flex-1 gap-[6px]">
              <span className="h-[10px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ width: `${title}%` }} />
              <span className="h-[8px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ width: `${line}%` }} />
            </span>
            <span className="h-[17px] w-[26px] shrink-0 rounded-[2px] bg-[color:var(--line-soft)]" />
          </div>
        );
      })}
    </div>
  );
}

// Why a check stopped short, in a phrase.
export const stoppedWords = (detail) => STOPPED_LABEL[detail] || "it ended early";
